import React, { useState } from 'react'
import config from '../config'

interface Props {
  isOpen: boolean
  onClose: () => void
}

export default function ChangePasswordModal({ isOpen, onClose }: Props) {
  const [step, setStep] = useState<'verify' | 'change'>('verify')
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)

  if (!isOpen) return null

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      // 현재 비밀번호로 로그인 시도하여 검증
      const token = localStorage.getItem('access_token')
      const resp = await fetch(`${config.API_BASE_URL}/auth/verify-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ current_password: currentPassword }),
      })
      if (!resp.ok) {
        setError('현재 비밀번호가 일치하지 않습니다.')
        return
      }
      setStep('change')
    } catch {
      setError('서버 연결에 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (newPassword.length < 6) {
      setError('새 비밀번호는 6자 이상이어야 합니다.')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('새 비밀번호가 일치하지 않습니다.')
      return
    }

    setLoading(true)
    try {
      const token = localStorage.getItem('access_token')
      const resp = await fetch(`${config.API_BASE_URL}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      })
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}))
        setError(data.detail || '비밀번호 변경에 실패했습니다.')
        return
      }
      setSuccess(true)
    } catch {
      setError('서버 연결에 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setStep('verify')
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setError('')
    setSuccess(false)
    onClose()
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px', border: '1px solid #d1d5db',
    borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box',
  }

  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px',
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
      }}
      onClick={handleClose}
    >
      <div
        style={{
          backgroundColor: 'white', borderRadius: '12px', padding: '32px',
          width: '400px', maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1f2937', marginBottom: '24px' }}>
          비밀번호 변경
        </h2>

        {step === 'verify' && (
          <form onSubmit={handleVerify}>
            <div style={{ marginBottom: '24px' }}>
              <label style={labelStyle}>현재 비밀번호</label>
              <input
                type="password"
                value={currentPassword}
                onChange={e => { setCurrentPassword(e.target.value); setError('') }}
                required
                autoFocus
                style={inputStyle}
                placeholder="현재 비밀번호를 입력하세요"
              />
            </div>

            {error && (
              <div style={{ color: '#dc2626', fontSize: '13px', marginBottom: '16px' }}>{error}</div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={handleClose} style={{
                padding: '10px 20px', backgroundColor: '#f3f4f6', color: '#374151',
                border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', cursor: 'pointer',
              }}>취소</button>
              <button type="submit" disabled={loading} style={{
                padding: '10px 20px', backgroundColor: '#2563eb', color: 'white',
                border: 'none', borderRadius: '8px', fontSize: '14px',
                cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1,
              }}>{loading ? '확인 중...' : '다음'}</button>
            </div>
          </form>
        )}

        {step === 'change' && (
          <form onSubmit={handleChange}>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>새 비밀번호</label>
              <input
                type="password"
                value={newPassword}
                onChange={e => { setNewPassword(e.target.value); setError('') }}
                required
                autoFocus
                style={inputStyle}
                placeholder="6자 이상 입력하세요"
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={labelStyle}>새 비밀번호 확인</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={e => { setConfirmPassword(e.target.value); setError('') }}
                required
                style={inputStyle}
                placeholder="새 비밀번호를 다시 입력하세요"
              />
            </div>

            {error && (
              <div style={{ color: '#dc2626', fontSize: '13px', marginBottom: '16px' }}>{error}</div>
            )}
            {success && (
              <div style={{ color: '#059669', fontSize: '13px', marginBottom: '16px' }}>비밀번호가 변경되었습니다.</div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              {!success && (
                <button type="button" onClick={() => { setStep('verify'); setError('') }} style={{
                  padding: '10px 20px', backgroundColor: '#f3f4f6', color: '#374151',
                  border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', cursor: 'pointer',
                }}>이전</button>
              )}
              {success ? (
                <button type="button" onClick={handleClose} style={{
                  padding: '10px 20px', backgroundColor: '#059669', color: 'white',
                  border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'pointer',
                }}>닫기</button>
              ) : (
                <button type="submit" disabled={loading} style={{
                  padding: '10px 20px', backgroundColor: '#2563eb', color: 'white',
                  border: 'none', borderRadius: '8px', fontSize: '14px',
                  cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1,
                }}>{loading ? '변경 중...' : '변경'}</button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
